import _ from 'lodash';
import { useCallback, useContext, useMemo } from 'react';
import { ENCAISSEMENT, MODES_REGLEMENTS } from '@/constants';
import { ModeReglementContext } from '@/contexts';
import type { CacheMetadataType } from '@/types';
import { isCacheValid } from '@/utils/cache';
import { useRegieStore } from '@/zustand';
import useCyclesStore from '@/zustand/accueil/useCyclesStore';
import useEncaisseStore from '@/zustand/accueil/useEncaisseStore';
import useIndicateursStore from '@/zustand/accueil/useIndicateursStore';
import useRecuStatsStore from '@/zustand/accueil/useRecuStatsStore';

const { FRAICHEUR_MINIMALE_INDICATEURS } = ENCAISSEMENT;

/**
 * Hook du rafraîchissement automatique des quatre stores d'indicateurs (encaisse, indicateurs
 * clés, cycles, statistiques des reçus), à brancher sur useAutoRefresh.
 *
 * `rafraichir` recharge chaque store, sauf s'il vient de l'être (FRAICHEUR_MINIMALE_INDICATEURS) :
 * à l'entrée sur l'écran, les hooks de données ont souvent déjà relancé le chargement.
 * `pret` passe à vrai une fois la régie et ses modes de règlement connus, nécessaires aux
 * statistiques des reçus.
 *
 * Il ne s'abonne pas aux données des stores : le composant qui l'utilise n'est pas re-rendu à
 * chaque rafraîchissement.
 */
const useRafraichirIndicateurs = () => {
  const regieId = useRegieStore((state) => state.regie?.identifiant);
  const periodicite = useRegieStore((state) => state.regie?.periodiciteArrete || 'Mensuel');
  const regieProlongee = useRegieStore((state) => state.regie?.regieProlongee || false);
  const joursSupplementaires = useRegieStore(
    (state) => state.regie?.joursEncaissementSupplementaires || 0,
  );
  const { regieModesReglement } = useContext(ModeReglementContext);

  const hasCBI = useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.CARTE_BANCAIRE_INTERNET }),
    [regieModesReglement],
  );

  const hasPRI = useMemo(
    () => _.some(regieModesReglement, { code: MODES_REGLEMENTS.PRELEVEMENT_INTERNET }),
    [regieModesReglement],
  );

  const pret = !_.isNil(regieId) && !_.isNil(regieModesReglement);

  const rafraichir = useCallback(() => {
    if (!regieId) return;

    const estFrais = ({ cache }: { cache: CacheMetadataType }) =>
      isCacheValid(cache.timestamp, FRAICHEUR_MINIMALE_INDICATEURS, cache.regieId, regieId);

    const encaisse = useEncaisseStore.getState();
    if (!estFrais(encaisse)) encaisse.fetch(regieId, true);

    const indicateurs = useIndicateursStore.getState();
    if (!estFrais(indicateurs)) indicateurs.fetch(regieId, true);

    const cycles = useCyclesStore.getState();
    if (!estFrais(cycles)) {
      cycles.fetch(regieId, periodicite, regieProlongee, joursSupplementaires, true);
    }

    // Attendre que les modes de règlement soient chargés
    const recuStats = useRecuStatsStore.getState();
    if (!_.isEmpty(regieModesReglement) && !estFrais(recuStats)) {
      recuStats.fetch(regieId, hasCBI, hasPRI, true);
    }
  }, [
    regieId,
    periodicite,
    regieProlongee,
    joursSupplementaires,
    regieModesReglement,
    hasCBI,
    hasPRI,
  ]);

  return { rafraichir, pret };
};

export default useRafraichirIndicateurs;
